import {
  of as observableOf,
  throwError as observableThrowError,
  Observable,
  combineLatest,
} from 'rxjs';

import {shareReplay, catchError, map, mergeMap, switchMap, take} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpHeaders, HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {User} from './user';
import {LanguageService} from '../../language/language.service';
import {SupportedLocaleConfigurationService} from '../uiconfiguration/supportedlocaleconfiguration.service';
import {MsalService} from '@azure/msal-angular';
import {LayoutService} from '../../layout/layout.service';
import {Endpoint} from '../../layout/endpoint';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';

@Injectable()
export class CurrentUserService {
  private headers: HttpHeaders;

  constructor(
    private authService: MsalService,
    private http: HttpClient,
    private languageService: LanguageService,
    private locService: SupportedLocaleConfigurationService,
    private layoutService: LayoutService,
    private envConfig: EnvironmentConfigService,
  ) {}

  /** MIM only: the Administrators set, whose explicit members are the administrators. */
  private getAdministratorParams(): HttpParams {
    return new HttpParams()
      .append(
        'filter',
        '/' +
          this.envConfig.getObjectTypeName('Set') +
          '[' +
          this.envConfig.getAttributeName('ObjectID') +
          "='10000005-1111-45b5-ad13-2764d866c000']",
      )
      .append('attributes', this.envConfig.getAttributeName('ExplicitMember'));
  }

  public getCurrentUser(): Observable<User> {
    this.headers = new HttpHeaders({'Content-Type': 'application/json'});

    return combineLatest([
      this.languageService.getLanguage(this.locService.getSupportedLocaleConfiguration()),
      this.layoutService.endpoint$.pipe(take(1)),
    ]).pipe(
      switchMap(([language, env]: [string, Endpoint]) => {
        let params = new HttpParams();
        let actionUrl: string;

        if (env.isCloud()) {
          const activeAccount = this.authService.instance.getActiveAccount();
          const oid =
            activeAccount.localAccountId ||
            activeAccount.homeAccountId?.split('.')[0] ||
            activeAccount.idTokenClaims?.['oid'] ||
            activeAccount.idTokenClaims?.['sub'] ||
            'Keine OID verfügbar';

          actionUrl = 'v2/resources/user/oid/' + oid;
        } else {
          actionUrl = 'v2/currentuser/';
          params = params.append('locale', language);
        }

        return this.http
          .get(env.getUrl() + actionUrl, {
            headers: this.headers,
            params: params,
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          })
          .pipe(
            map(response => this.toUser(response, env)),
            catchError(CurrentUserService.handleError),
          );
      }),
    );
  }

  private static handleError(error: HttpErrorResponse) {
    console.error(error);
    return observableThrowError(error || 'Server error');
  }

  private toUser(r: any, endpoint: Endpoint): User {
    let darkmode = false;
    const darkModeAttr = this.envConfig.getAttributeName('DarkMode');
    if (r[darkModeAttr]) {
      darkmode = r[darkModeAttr];
    }
    let user = <User>{
      id: r[this.envConfig.getAttributeName('ObjectID')],
      displayname: r[this.envConfig.getAttributeName('DisplayName')],
      description: r[this.envConfig.getAttributeName('Description')],
      domain: r[this.envConfig.getAttributeName('Domain')],
      timezone: r[this.envConfig.getAttributeName('TimeZone')],
      darkmode: darkmode,
    };
    const photoAttr = this.envConfig.getAttributeName('Photo');
    if (r[photoAttr] != null) {
      user.photo = r[photoAttr];
    }
    let obs: Observable<boolean>;
    if (endpoint.isCloud()) {
      obs = observableOf(
        r[this.envConfig.getAttributeName('Class')] === 'IdentityDirectorAdministrator',
      );
    } else {
      const explicitMemberAttr = this.envConfig.getAttributeName('ExplicitMember');
      const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
      obs = this.http
        .get(endpoint.getUrl() + 'v2/resources/', {
          headers: this.headers,
          params: this.getAdministratorParams(),
          ...(!endpoint.isCloud() ? {withCredentials: true} : {}),
        })
        .pipe(
          map((response: any) => response.Results[0]),
          map(result => {
            if (result != null && result[explicitMemberAttr] != null) {
              if (-1 !== result[explicitMemberAttr].indexOf(r[objectIdAttr])) return true;
            }
            return false;
          }),
          shareReplay(1),
          catchError(CurrentUserService.handleError),
        );
    }
    user.administrator = obs;

    return user;
  }
}
